import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, AlertTriangle, Trash2, Sparkles, UserX, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AdminLayout from '@/components/admin/AdminLayout';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  running: { label: 'Running…', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  ok: { label: 'Complete', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  partial: { label: 'Partial', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  failed: { label: 'Failed', color: 'bg-rose-100 text-rose-800 border-rose-300' },
};

export default function AdminWave2Ops() {
  const [statusByOp, setStatusByOp] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyOp, setBusyOp] = useState(null);

  const authHeaders = () => {
    const sess = localStorage.getItem('token');
    return sess ? { Authorization: `Bearer ${sess}` } : {};
  };
  // ESLint react-hooks/exhaustive-deps note: `refresh` and `authHeaders` are
  // stable (no closure over component state). API_URL/axios are module-level.
  // setters are stable per React contract. Empty dep is intentional.

  const refresh = useCallback(async () => {
    try {
      const sess = localStorage.getItem('token');
      const headers = sess ? { Authorization: `Bearer ${sess}` } : {};
      const res = await axios.get(`${API_URL}/api/admin/wave2/status`, { headers });
      const next = {};
      (res.data.ops || []).forEach((row) => { next[row.op] = row; });
      setStatusByOp(next);
    } catch (e) {
      // Silent — endpoint may not exist on prod yet until redeploy
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    // Auto-poll every 5s so long-running ops update live
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [refresh]);

  const runOp = async (op, label, confirmMsg) => {
    if (!window.confirm(confirmMsg)) return;
    setBusyOp(op);
    try {
      await axios.post(`${API_URL}/api/admin/wave2/${op}`, {}, { headers: authHeaders() });
      toast.success(`${label} started — running in background`);
      setTimeout(refresh, 1000);
    } catch (e) {
      const detail = e?.response?.data?.detail
        || e?.response?.statusText
        || e?.message
        || 'network error';
      const code = e?.response?.status ? ` (HTTP ${e.response.status})` : '';
      toast.error(`${label} failed: ${detail}${code}`, { duration: 7000 });
    } finally {
      setBusyOp(null);
    }
  };

  const renderOpCard = ({ op, title, desc, icon, danger, button, confirm }) => {
    const status = statusByOp[op];
    const badge = status && STATUS_BADGE[status.status];
    return (
      <div className={`border rounded-lg p-5 ${danger ? 'border-rose-200 bg-rose-50/30' : 'border-slate-200 bg-white'}`} data-testid={`wave2-card-${op}`}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            {icon}
            <h3 className="font-bold text-slate-900">{title}</h3>
          </div>
          {badge && (
            <span className={`text-[10px] uppercase font-bold tracking-wide px-2 py-1 rounded border ${badge.color}`}>
              {badge.label}
            </span>
          )}
        </div>
        <p className="text-sm text-slate-600 mb-4 leading-snug">{desc}</p>

        {status && status.status === 'running' && (
          <div className="mb-4 text-xs bg-blue-50 border border-blue-200 rounded p-2 text-blue-900 space-y-0.5">
            <div className="flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" />
              <b>{status.progress || 'Working…'}</b>
            </div>
            {typeof status.products_seeded === 'number' && (
              <div>Products seeded so far: <b>{status.products_seeded}/12</b></div>
            )}
          </div>
        )}

        {status && (status.status === 'ok' || status.status === 'partial') && (
          <div className="mb-4 text-xs bg-emerald-50 border border-emerald-200 rounded p-2 text-emerald-900 space-y-0.5">
            {op === 'wipe-seed-data' && (
              <>
                <div>Deleted creators: <b>{status.deleted_creators ?? 0}</b></div>
                <div>Deleted products: <b>{status.deleted_products ?? 0}</b></div>
              </>
            )}
            {op === 'seed-ledger-workshop' && (
              <>
                <div>Storefront: <b>{status.store_slug}</b></div>
                <div>Products seeded: <b>{status.products_seeded ?? 0}/12</b></div>
                {status.errors && status.errors.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-rose-700 font-bold">
                      Errors ({status.errors.length}) — click to expand
                    </summary>
                    <ul className="mt-1 ml-3 list-disc text-rose-800">
                      {status.errors.map((e, i) => (
                        <li key={i} className="font-mono text-[10px]">
                          <b>{e.slug}:</b> {e.error}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </>
            )}
            {op === 'purge-test-users' && (
              <>
                <div>Deleted users: <b>{status.deleted_users ?? 0}</b></div>
                <div>Rows cleaned across: <b>{Object.keys(status.purged_rows || {}).length}</b> collections</div>
              </>
            )}
            {op === 'reset-launch-state' && (
              <>
                {status.storefront_record_updated && (
                  <div>Storefront record refreshed ✓</div>
                )}
                <div>Workshop products zeroed: <b>{status.workshop_products_zeroed ?? 0}</b></div>
                <div>Real products approved: <b>{status.unapproved_products_approved ?? 0}</b></div>
              </>
            )}
            {op === 'regen-hero-images' && (
              <>
                <div>Regenerated: <b>{status.completed ?? 0}/12</b></div>
                {status.failed && status.failed.length > 0 && (
                  <div className="text-rose-700">Failed: {status.failed.length}</div>
                )}
              </>
            )}
            {op === 'drift-check' && (
              <>
                <div>Storefronts checked: <b>{status.checked ?? 0}</b></div>
                <div>Drifts found + healed: <b>{status.auto_healed ?? 0}</b></div>
                {status.drifts && status.drifts.length > 0 && (
                  <details className="mt-1">
                    <summary className="cursor-pointer">Drift detail</summary>
                    <pre className="mt-1 text-[10px] bg-white p-1 rounded">{JSON.stringify(status.drifts, null, 2)}</pre>
                  </details>
                )}
                {status.traceback && (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-rose-700 font-bold">Traceback</summary>
                    <pre className="mt-1 text-[9px] bg-rose-50 p-2 rounded overflow-x-auto">{status.traceback}</pre>
                  </details>
                )}
              </>
            )}
            <div className="text-emerald-700 opacity-60 pt-1">Updated: {status.updated_at?.slice(0, 19).replace('T', ' ')}</div>
          </div>
        )}

        {status && status.status === 'failed' && (
          <div className="mb-4 text-xs bg-rose-100 border border-rose-300 rounded p-2 text-rose-900">
            <b>Failed:</b> <span className="font-mono">{status.error}</span>
          </div>
        )}

        <Button
          onClick={() => runOp(op, title, confirm)}
          disabled={busyOp === op || status?.status === 'running'}
          variant={danger ? 'destructive' : 'default'}
          className="w-full"
          data-testid={`run-${op}-btn`}
        >
          {(busyOp === op || status?.status === 'running') ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running…</>
          ) : (
            <>{button}</>
          )}
        </Button>
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-12 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-5xl mx-auto" data-testid="admin-wave2-ops">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Sparkles className="w-7 h-7 text-amber-500" />
              Wave 2 Production Sync
            </h1>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              One-click ops to bring this database in line with the approved Wave 2 launch state.
              Run them <b>in order</b>: 1️⃣ wipe → 2️⃣ seed → 3️⃣ purge. Safe to re-run; each op is idempotent.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={refresh} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex gap-3" data-testid="prod-warning">
          <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <b>Heads up:</b> these endpoints write directly to whichever database this server is connected to.
            Run them on production after the founder has validated preview looks right.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {renderOpCard({
            op: 'wipe-seed-data',
            title: '1. Wipe legacy seed data',
            desc: 'Deletes any creator with 50+ products and zero completed orders — those are the pre-launch demo creators. Skips the admin storefront, TheLedger Workshop, and any creator with real order history.',
            icon: <Trash2 className="w-5 h-5 text-rose-600" />,
            danger: true,
            button: 'Wipe seed creators + products',
            confirm: 'Wipe legacy seed creators + their products from this database? This is reversible only via DB restore.',
          })}
          {renderOpCard({
            op: 'seed-ledger-workshop',
            title: '2. Seed TheLedger Workshop',
            desc: 'Creates the house storefront, generates hero images via Nano Banana, polished PDFs, copy, Custom GPT files. Inserts all 12 approved products + 3 launch coupons. Takes 5-10 min in the background.',
            icon: <Sparkles className="w-5 h-5 text-emerald-600" />,
            danger: false,
            button: 'Seed 12 products + coupons',
            confirm: 'Seed TheLedger Workshop + 12 products + 3 launch coupons? Safe to re-run — updates if already present.',
          })}
          {renderOpCard({
            op: 'purge-test-users',
            title: '3. Purge test users',
            desc: 'Deletes every user except admin (ailedgerllc@gmail.com), jakecolson35@gmail.com, and the house storefront user. Also cleans 25+ related collections (sessions, orders, referrals, etc.).',
            icon: <UserX className="w-5 h-5 text-amber-600" />,
            danger: true,
            button: 'Purge test users + related rows',
            confirm: 'Delete every user except admin + Jake + the house storefront, plus their related rows across the DB?',
          })}
          {renderOpCard({
            op: 'reset-launch-state',
            title: '4. Reset launch state',
            desc: 'Zeros total_sales / rating / total_reviews on every Workshop product (kills any pre-existing fake-launch numbers). Also approves any real-creator products stuck without an approval_status so they show up in trending and just-launched.',
            icon: <RefreshCw className="w-5 h-5 text-slate-600" />,
            danger: false,
            button: 'Zero sales + approve real products',
            confirm: 'Zero Workshop product sales/reviews and approve all unapproved real-creator products?',
          })}
          {renderOpCard({
            op: 'regen-hero-images',
            title: '5. Regen hero images',
            desc: 'Deletes existing /products/{slug}.{png|jpg} files and regenerates all 12 with the latest text-free Nano Banana prompt. New files are saved with the correct extension based on the bytes returned (usually .jpg). Use this when product names overflow the image (e.g., MailTriage / Hookline). Takes 2-3 min.',
            icon: <Sparkles className="w-5 h-5 text-slate-600" />,
            danger: false,
            button: 'Regen all 12 hero images',
            confirm: 'Delete and regenerate all 12 Workshop product hero images?',
          })}
          {renderOpCard({
            op: 'drift-check',
            title: '6. Drift guardrail',
            desc: 'Compares live storefront records against wave2_specs.STOREFRONT. Auto-heals any drift in store_name, business_name, bio, avatar, banner, or refund_policy. Runs automatically every day at 7am PT — this button triggers it on demand.',
            icon: <AlertTriangle className="w-5 h-5 text-slate-600" />,
            danger: false,
            button: 'Run drift check now',
            confirm: 'Run the storefront drift guardrail now? Will auto-heal any divergence.',
          })}
        </div>

        <div className="mt-8 text-xs text-slate-500 text-center">
          Status auto-refreshes every 5 seconds while ops are running.
        </div>
      </div>
    </AdminLayout>
  );
}
